import { STATES } from "#src/constants/states-and-countries.ts";
import unemploymentReasons from "#src/constants/unemployment-reasons.ts";
import {
  assertSavableProperty,
  type SavableProperty,
  savablePropertyRequestWrapper,
  useSavableProperty,
} from "#src/composables/savable-property.composable.ts";
import {
  validateBoolean,
  validateInList,
  validateNumber,
  validateText,
} from "#src/composables/savable-property-validators.ts";

import { dateToYears, generateUuid, yearsToDate } from "#src/util/helpers.ts";
import {
  buildRequestPayload,
  defineStore,
  generateInitialState,
  resetState,
  type SetupStore,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  occupationDelete,
  occupationUpdate,
} from "#src/services/http-individuals.service.ts";

import { useInsuredStore } from "#src/stores/insured.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { type InsuredType } from "#src/types/Insured.ts";
import { type RawInsuredOccupation } from "#src/types/InsuredOccupation.ts";

interface InsuredOccupationState {
  occupation_id: number | null;
  employer_name: SavableProperty<string>;
  employer_street_address: SavableProperty<string>;
  employer_city: SavableProperty<string>;
  employer_state: SavableProperty<string>;
  employer_zip: SavableProperty<string>;
  is_employed: SavableProperty<boolean>;
  occupation_name: SavableProperty<string>;
  occupation_years: SavableProperty<number>;
}

const REQ_GROUP = {
  EMPLOYER: "EMPLOYER",
  EMPLOYER_ADDRESS: "EMPLOYER_ADDRESS",
  OCCUPATION: "OCCUPATION",
};

export type ReqGroup = (typeof REQ_GROUP)[keyof typeof REQ_GROUP];

function initialState(
  insuredType: InsuredType,
  pinia: Pinia
): InsuredOccupationState {
  return {
    occupation_id: null,
    employer_name: useSavableProperty({
      requestMap: "employer.name",
      rules: {
        validLength: validateText({ minLength: 1, maxLength: 100 }),
      },
    }),
    employer_street_address: useSavableProperty({
      requestMap: "employer.address.street_address",
      rules: {
        validLength: validateText({ minLength: 1, maxLength: 255 }),
      },
    }),
    employer_city: useSavableProperty({
      requestMap: "employer.address.city",
      rules: {
        validLength: validateText({ minLength: 1, maxLength: 255 }),
      },
    }),
    employer_state: useSavableProperty({
      requestMap: "employer.address.state",
      rules: {
        inList: validateInList(STATES.map(s => s.value)),
      },
    }),
    employer_zip: useSavableProperty({
      requestMap: "employer.address.zip",
      rules: {
        validLength: validateText({ exactLength: 5 }),
      },
    }),
    is_employed: useSavableProperty({
      rules: {
        isTrueOrFalse: validateBoolean(),
      },
    }),
    occupation_name: useSavableProperty({
      requestMap: "occupation.occupation",
      rules: {
        validLength: validateText({ minLength: 1, maxLength: 255 }),
        inList: {
          v() {
            const store = useInsuredOccupationStore(insuredType, pinia);
            if (store.is_employed) return true;
            if (!(typeof this.model === "number")) return false;
            return unemploymentReasons.includes(this.model);
          },
          message: "Must be a valid unemployment reason",
        },
      },
    }),
    occupation_years: useSavableProperty({
      requestMap: "occupation.start_date",
      requestFormatter: yearsToDate,
      rules: {
        isNumber: validateNumber({ greaterThanOrEqualTo: 0 }),
        isLessThanAge: {
          v() {
            const insured = useInsuredStore(insuredType, pinia);
            if (!(typeof this.model === "number")) return false;
            if (!(typeof insured.age === "number")) return false;
            return this.model <= insured.age;
          },
          message: () => {
            const store = useInsuredStore(insuredType, pinia);
            return `Cannot be greater than ${store.age}`;
          },
        },
      },
    }),
  };
}

interface InsuredOccupation
  extends SetupStore,
    ToSetupState<InsuredOccupationState> {
  setFromEApp(occupation: RawInsuredOccupation): void;
  saveAttributes(
    attributes: (keyof InsuredOccupationState)[]
  ): Promise<unknown>;
  deleteOccupation(): Promise<void>;
}

function initializeInsuredOccupation(
  insuredType: InsuredType,
  pinia: Pinia
): InsuredOccupation {
  const state = generateInitialState<InsuredOccupationState>(
    initialState(insuredType, pinia)
  );
  function $reset() {
    resetState<InsuredOccupationState>(state, initialState(insuredType, pinia));
  }

  function setFromEApp(occupation: RawInsuredOccupation) {
    if (!occupation) return;
    if (occupation.occupation) {
      state.is_employed.value.load(
        !unemploymentReasons.some(v => v === occupation.occupation)
      );
    } else if (occupation?.employer?.name) {
      state.is_employed.value.load(true);
    }

    state.occupation_id.value = occupation?.id;
    if (occupation?.occupation) {
      state.occupation_name.value.load(occupation.occupation);
    }
    if (occupation?.start_date) {
      state.occupation_years.value.load(dateToYears(occupation.start_date));
    }

    if (!occupation.employer) return;

    const employer = occupation.employer;
    if (employer?.name) {
      state.employer_name.value.load(employer.name);
    }

    const eAddress = employer?.business_address;
    if (eAddress?.street_address) {
      state.employer_street_address.value.load(eAddress.street_address);
    }
    if (eAddress?.city) {
      state.employer_city.value.load(eAddress.city);
    }
    if (eAddress?.state) {
      state.employer_state.value.load(eAddress.state);
    }
    if (eAddress?.zip) {
      state.employer_zip.value.load(eAddress.zip);
    }
  }

  async function saveAttributes(attributes: (keyof InsuredOccupationState)[]) {
    const composableAttributes: SavableProperty<any>[] = [];
    const dtoData: Record<string, any> = {};
    const dtoConfig: Record<string, string> = {};

    attributes.forEach(a => {
      const attribute = state[a].value;
      try {
        assertSavableProperty(attribute);
        composableAttributes.push(attribute);
      } catch (e) {
        return;
      }

      const value = attribute.format();
      dtoData[a] = value;
      dtoConfig[a] = attribute.requestMap as string;
    });

    const body = buildRequestPayload(dtoConfig, dtoData);
    const insured = useInsuredStore(insuredType, pinia);
    const insuredId = insured.id;
    if (!insuredId) throw "Missing Insured ID";

    const func = async () => {
      const occupation = await occupationUpdate(pinia, insuredId, body);
      state.occupation_id.value = occupation.id;
      return occupation;
    };

    return savablePropertyRequestWrapper<RawInsuredOccupation, any, any, any>(
      func,
      {
        composableAttributes,
        body,
        uuid: generateUuid(),
      }
    );
  }

  async function deleteOccupation() {
    if (!state.occupation_id.value) return;

    state.occupation_id.value = null;
    const insured = useInsuredStore(insuredType, pinia);
    if (!insured.id) throw "Missing Insured ID";
    await occupationDelete(pinia, insured.id);
  }

  return {
    ...state,
    $reset,
    setFromEApp,
    saveAttributes,
    deleteOccupation,
  };
}

export function useInsuredOccupationStore(
  insuredType: InsuredType,
  pinia: Pinia,
  hot?: StoreGeneric
) {
  return defineStore<string, InsuredOccupation>(
    `${insuredType}-occupation`,
    () => {
      return initializeInsuredOccupation(insuredType, pinia);
    }
  )(pinia, hot);
}
